<template>
  <Teleport to="body">
    <div class="fixed bottom-4 right-4 z-50 space-y-2">
      <TransitionGroup
        name="toast"
        tag="div"
        class="space-y-2"
      >
        <div
          v-for="toast in toasts"
          :key="toast.id"
          :class="[
            'max-w-sm p-4 rounded-lg shadow-lg border',
            {
              'bg-green-50 border-green-200 text-green-800': toast.type === 'success',
              'bg-red-50 border-red-200 text-red-800': toast.type === 'error',
              'bg-yellow-50 border-yellow-200 text-yellow-800': toast.type === 'warning',
              'bg-blue-50 border-blue-200 text-blue-800': toast.type === 'info',
            }
          ]"
        >
          <div class="flex items-start">
            <div class="flex-shrink-0">
              <CheckCircle
                v-if="toast.type === 'success'"
                class="w-5 h-5 text-green-400"
              />
              <XCircle
                v-else-if="toast.type === 'error'"
                class="w-5 h-5 text-red-400"
              />
              <AlertTriangle
                v-else-if="toast.type === 'warning'"
                class="w-5 h-5 text-yellow-400"
              />
              <Info
                v-else
                class="w-5 h-5 text-blue-400"
              />
            </div>
            <div class="ml-3 w-0 flex-1">
              <p class="text-sm font-medium">
                {{ toast.title }}
              </p>
              <p v-if="toast.message" class="mt-1 text-sm">
                {{ toast.message }}
              </p>
            </div>
            <div class="ml-4 flex-shrink-0 flex">
              <button
                @click="removeToast(toast.id)"
                class="bg-transparent rounded-md inline-flex text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <X class="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import {
  CheckCircle,
  XCircle,
  AlertTriangle,
  Info,
  X
} from 'lucide-vue-next'

interface Toast {
  id: string
  type: 'success' | 'error' | 'warning' | 'info'
  title: string
  message?: string
  duration?: number
}

const toasts = ref<Toast[]>([])

const addToast = (toast: Omit<Toast, 'id'>) => {
  const id = Math.random().toString(36).substr(2, 9)
  const newToast = { ...toast, id }

  toasts.value.push(newToast)

  // Auto-remove after duration
  const duration = toast.duration || 5000
  setTimeout(() => {
    removeToast(id)
  }, duration)

  return id
}

const removeToast = (id: string) => {
  const index = toasts.value.findIndex(toast => toast.id === id)
  if (index > -1) {
    toasts.value.splice(index, 1)
  }
}

// Expose methods globally (you can use provide/inject or composable)
;(window as any).toast = {
  success: (title: string, message?: string) =>
    addToast({ type: 'success', title, message }),
  error: (title: string, message?: string) =>
    addToast({ type: 'error', title, message }),
  warning: (title: string, message?: string) =>
    addToast({ type: 'warning', title, message }),
  info: (title: string, message?: string) =>
    addToast({ type: 'info', title, message }),
}

defineExpose({
  addToast,
  removeToast,
})
</script>

<style scoped>
.toast-enter-active {
  transition: all 0.3s ease-out;
}

.toast-leave-active {
  transition: all 0.3s ease-in;
}

.toast-enter-from {
  transform: translateX(100%);
  opacity: 0;
}

.toast-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

.toast-move {
  transition: transform 0.3s ease;
}
</style>